import {AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, inject, Input} from '@angular/core';
import {RowExpansionService} from '@aviary/components/table/row-expansion/row-expansion.service';
import {RotatingChevronComponent} from '@aviary/components/rotating-chevron/rotating-chevron.component';
import {ExpansionToggleStyle} from '@aviary/components/table/table.component.types';
import {TABLE_EXPANSION_TOGGLE_STYLE} from '@aviary/injection-tokens/app-provided.tokens';
import {ViewportSelectors} from '@aviary/store/viewport/viewport.selectors';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {OutlinedButtonComponent} from '@aviary/buttons/outlined-button/outlined-button.component';
import {IconButtonComponent} from '@aviary/buttons/icon-button/icon-button.component';

@Component({
  selector: 'mx-expansion-toggle',
  template: `
    @if (expansionToggleStyle === 'ROTATING_CHEVRON') {
      <mx-rotating-chevron [orientation]="isExpanded ? 'down' : 'right'" (click)="onToggleExpansion()"/>
    } @else {
      @let icon = isExpanded ? 'remove' : 'add';
      @let tooltip = isExpanded ? 'See less' : 'See more';

      @if (viewportSelectors.isSm | select) {
        <mx-outlined-button [startIcon]="icon" [tooltip]="tooltip" (click)="onToggleExpansion()"/>
      } @else {
        <mx-icon-button [icon]="icon" [tooltip]="tooltip" (click)="onToggleExpansion()"/>
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RotatingChevronComponent, SelectPipe, OutlinedButtonComponent, IconButtonComponent]
})
export class ExpansionToggleComponent<RowObject> implements AfterViewInit {
  @Input() rowObject: RowObject;

  public readonly expansionToggleStyle: ExpansionToggleStyle = inject(TABLE_EXPANSION_TOGGLE_STYLE);
  public readonly viewportSelectors = ViewportSelectors;
  private readonly elementReference: ElementRef = inject(ElementRef);
  private readonly rowExpansionService: RowExpansionService<RowObject> = inject(RowExpansionService);
  private parentTableRow: HTMLTableRowElement;

  public ngAfterViewInit(): void {
    const parentTableRow: HTMLTableRowElement | null = (this.elementReference.nativeElement as HTMLElement).closest('tr');
    if (!parentTableRow) throw Error('ExpansionToggleComponent needs to be inside a <tr> element');
    this.parentTableRow = parentTableRow;
  }

  public get isExpanded(): boolean {
    return this.rowExpansionService.isExpanded(this.rowObject);
  }

  public onToggleExpansion(): void {
    this.rowExpansionService.toggleExpansionFor(this.rowObject, this.parentTableRow);
  }
}
